import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Optional, Output, SimpleChanges } from '@angular/core';
import { Choice, ChoiceQuestion } from '@interfaces/questions';
import { Subscription } from 'rxjs';
import { WorkflowItemKeyDirective } from 'src/app/workflow/directives/workflow-item-key/workflow-item-key.directive';
import { SessionItemData } from 'src/app/workflow/services/session-item-data';
import { NzJustify } from 'ng-zorro-antd/grid';
import { WorkflowItemDirective } from '@workflow/directives/workflow-item/workflow-item.directive';

interface ChoiceEntry {
	readonly choice: Choice;
	selected: boolean;
	deactivated: boolean;
}
@Component({
	selector: 'workflow-multiple-choice',
	templateUrl: './multiple-choice.component.html',
	styleUrls: ['./multiple-choice.component.scss']
})
export class MultipleChoiceComponent implements OnInit, OnDestroy, OnChanges {

	@Input() question: ChoiceQuestion;
	@Input() layout: string;
	@Input() justify: NzJustify;
	@Input() isWizard = false;
	@Input() finishedAndLocked = false;
	@Input() isLocked = false;
	@Input() isTableDisplay: boolean;

	@Output() valid: EventEmitter<boolean> = new EventEmitter();

	validity: boolean;
	validClass: string;
	hasValidity: boolean;

	// Gewählte Auswahlmöglichkeit bei einfach-Selektion
	_selected: ChoiceEntry;
	// Lokaler State der Auswahlmöglichkeiten bei multiple-selections
	entries: ChoiceEntry[];

	private propertySubscription = new Subscription();
	private deactivateSubscription = new Subscription();

	constructor(@Optional() private workflowItemKey: WorkflowItemKeyDirective, @Optional() private workflowItemDirective: WorkflowItemDirective) {}

	ngOnInit(): void {
		this.propertySubscription.add(this.workflowItemKey.property$.subscribe(data => {
			if (!this.finishedAndLocked) {
				const choicesHaveKeys = this.entries?.some(entry => entry?.choice?.key);

				if (choicesHaveKeys) {
					const sessionData = new SessionItemData(data);
					for (const entry of this.entries) {
						if (entry?.choice.key) {
							const data = sessionData.getProperty(entry.choice.key);
							entry.selected = data !== null && data !== undefined;
						}
					}
				} else {
					this.selected = this.entries?.find(entry => entry?.choice?.value === data);
				}

				this.testNumberChoices();
			}
		}));
		this.propertySubscription.add(this.workflowItemKey.propertyFull$.subscribe(data => {
			if (this.finishedAndLocked) {
				const choicesHaveKeys = this.entries?.some(entry => entry?.choice?.key);

				if (choicesHaveKeys) {
					const sessionData = new SessionItemData(data);
					for (const entry of this.entries) {
						if (entry?.choice.key) {
							const data = sessionData.getProperty(entry.choice.key);
							entry.selected = data !== null && data !== undefined;
						}
					}
				} else {
					this.selected = this.entries?.find(entry => entry?.choice?.value === data);
				}

				this.valid.emit(true);
			}
		}));
	}

	ngOnDestroy(): void {
		this.deactivateSubscription.unsubscribe();
		this.propertySubscription.unsubscribe();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes?.question && Array.isArray(this.question?.choices)) {
			this.entries = this.question.choices.map(choice => {
				return {
					choice: choice,
					selected: false,
					deactivated: null,
				} as ChoiceEntry;
			});

			this.deactivateSubscription.unsubscribe();
			this.deactivateSubscription = new Subscription();
			for (const entry of this.entries) {
				if (entry.choice?.deactivateIf) {
					this.deactivateSubscription.add(this.workflowItemDirective.property$(entry.choice.deactivateIf).subscribe(deactivated => {
						entry.deactivated = !!deactivated;
						if (entry.deactivated && entry.selected) {
							this.toggle(entry);
						}
					}));
				}
			}
		}
		this.hasValidity = !!this.question?.maxSelections || !!this.question?.minSelections;
		this.testNumberChoices();
	}

	testNumberChoices(): void {
		if (!this.question.multipleSelections) {
			this.validity = !!this.selected || this.question.isAnswerOptional;
			this.valid.emit(this.validity);
		}
		else if (this.entries) {
			let minSelections = this.question.minSelections ? this.question.minSelections : 0;
			if (!this.question.isAnswerOptional && minSelections === 0) {
				minSelections = 1;
			}
			if (minSelections !== 0 || this.question.maxSelections) {
				const numSelected = this.entries.filter(entry => entry?.selected).length;
				const hasMinSelections = numSelected >= minSelections;
				const hasMaxSelections = !this.question.maxSelections || numSelected <= this.question.maxSelections;
				this.validity = hasMinSelections && hasMaxSelections;
			} else {
				this.validity = true;
			}
			this.valid.emit(this.validity);
		}
		this.setValidityClass();
	}

	private setValidityClass(): void {
		if (!this.hasValidity) {
			this.validClass = 'no-validity';
		} else if (this.validity) {
			this.validClass = 'valid valid-yes';
		} else {
			this.validClass = 'valid valid-no';
		}
	}

	get isMultipleSelection(): boolean {
		return this.question.multipleSelections;
	}

	get selected(): ChoiceEntry {
		if (this.isMultipleSelection)
			return undefined;
		return this._selected;
	}

	set selected(entry: ChoiceEntry) {
		if (!this.isMultipleSelection) {
			for (const e of this.entries) {
				e.selected = false;
			}
		}
		if (entry) {
			entry.selected = true;
		}
		this._selected = entry;
	}

	get selectedValueAsString(): string {
		return (this.selected?.choice?.value === undefined) ? undefined : `${this.selected.choice.value}`;
	}

	toggle(entry: ChoiceEntry): void {
		this.selected = undefined;
		entry.selected = !entry.selected;

		if (entry.selected) {
			const entriesNeedReset = this.entries // Wir setzen die Felder zurück, wenn
				.filter(e => e.choice?.exclusive) // In der Liste der exklusiven Einträge
				.some(exclusives => exclusives === entry || exclusives.selected); // Der aktuelle exklusiv ist oder einer selektiert ist.
			if (entriesNeedReset) {
				this.resetFields();
			}
			entry.selected = true;
		}

		this.testNumberChoices();
		this.updateValue();
	}

	private resetFields() {
		for (const e of this.entries) {
			e.selected = false;
		}
	}

	select(entryValue: string): void {
		this.selected = this.entries.find(entry => `${entry.choice.value}` === entryValue);
		this.testNumberChoices();
		this.updateValue();
	}

	updateResult(result: unknown): void {
		if (this.workflowItemKey) {
			this.workflowItemKey.setProperty(this.validity ? result : undefined);
		}
	}

	updateValue(): void {
		if (!this.finishedAndLocked) {
			const choicesHaveKeys = this.entries.some(entry => entry?.choice?.key);
			if (choicesHaveKeys) {
				const data = new SessionItemData();
				for (const entry of this.entries) {
					if (entry?.choice.key) {
						const value = entry.selected ? entry.choice.value : undefined;
						data.setProperty(entry.choice.key, value);
					}
				}
				this.updateResult(data.data);
			} else {
				const value = this.selected?.choice?.value;
				this.updateResult(value);
			}
		}
	}
}
